<template>
  <div class="w-full relative flex flex-col">
    <div class="w-full flex flex-col justify-center">
      <booking-steps />
      <div class="flex flex-col gap-4 w-full mt-4">
        <l-select
          v-if="locations"
          :options="locations"
          v-model="locationId"
          @update:model-value="onLocationChange"
          size="large"
          :fluid-size="true"
        >
          <template #value="{ selected }">
            <div
              v-if="selected && !Array.isArray(selected)"
              class="flex flex-col gap-1 cursor-pointer text-xs"
            >
              <div>
                {{ selected.label }}
              </div>
              <div class="text-n-300 text-xs">
                {{ selected.metadata.address }}
              </div>
            </div>
          </template>
          <template #option="{ option, selectOption }">
            <div
              class="flex flex-col gap-1 px-4 py-2 cursor-pointer"
              @click="
                selectOption(option, { value: option.value, selected: true })
              "
            >
              <div>{{ option.label }}</div>
              <div class="text-n-300 text-xs">
                {{ option.metadata.address }}
              </div>
            </div>
          </template>
        </l-select>
        <div
          class="flex flex-col w-full p-4 border border-n-50 rounded-lg"
          @click="step = 'diners'"
        >
          <div
            class="flex items-center justify-between font-body font-bold cursor-pointer"
          >
            {{ $t('booking.diners') }}
            <div
              v-if="step !== 'diners' && dinersComputed"
              class="flex items-center justify-center rounded-lg h-9 min-w-16 font-body text-sm font-normal bg-[--primary-color] text-n-0"
            >
              {{ dinersComputed }}
            </div>
          </div>
          <div
            v-if="step === 'diners'"
            class="w-full pt-4 mt-4 border-t border-n-50 flex flex-wrap gap-2"
          >
            <radio-button
              v-for="n in Math.min(totalMaxSeats, 9)"
              :key="n"
              v-model="dinersComputed"
              name="diners"
              :value="n"
              @click="dinersSelected()"
            >
              {{ n }}
            </radio-button>

            <radio-button
              v-if="totalMaxSeats > 9"
              @click="(showMoreDiners = true), (dinersComputed = null)"
              :model-value="showMoreDiners ? '+10' : null"
              name="more-diners"
              value="+10"
            >
              <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
              {{ `+10` }}
            </radio-button>

            <l-number-input
              v-if="showMoreDiners || (dinersComputed && dinersComputed > 9)"
              v-model="dinersComputed"
              :min="1"
              :max="totalMaxSeats"
              :step="1"
              class="w-full"
            />
          </div>
        </div>

        <div
          class="flex flex-col w-full p-4 border border-n-50 rounded-lg"
          :class="{
            'opacity-30 pointer-events-none': !diners
          }"
          @click="step = 'date'"
        >
          <div
            class="flex items-center justify-between font-body font-bold cursor-pointer"
          >
            {{ $t('booking.date') }}
            <div
              v-if="step !== 'date' && dateFormatted"
              class="flex items-center justify-center rounded-lg h-9 min-w-16 px-2 font-body text-sm font-normal bg-[--primary-color] text-n-0 capitalize"
            >
              {{ dateFormatted }}
            </div>
          </div>
          <div
            v-if="diners && step === 'date'"
            class="w-full pt-4 mt-4 border-t border-n-50"
          >
            <l-calendar
              v-model="dateComputed"
              :unavailable-dates="unavailableDates"
              :max-date="endOfMonth(visibleMonth)"
              :timezone="timezone"
              only-future
              @month-changed="month => (visibleMonth = month)"
            />
          </div>
        </div>

        <div
          class="flex flex-col w-full p-4 border border-n-50 rounded-lg"
          :class="{
            'opacity-30 pointer-events-none max-h-16': !dateComputed
          }"
          @click="step = 'time'"
        >
          <div
            class="flex items-center justify-between font-body font-bold cursor-pointer"
          >
            {{ $t('booking.hour') }}
            <div
              v-if="step !== 'time' && hourFormatted"
              class="flex items-center justify-center rounded-lg h-9 min-w-16 px-2 font-body text-sm font-normal bg-[--primary-color] text-n-0"
            >
              {{ hourFormatted }}
            </div>
          </div>

          <div
            v-if="dateComputed && step === 'time'"
            class="w-full pt-4 mt-4 border-t border-n-50 gap-4 flex flex-col"
            @click="checkNextStep()"
          >
            <div v-for="(hours, dayZone) in dayAvailability" :key="dayZone">
              <div class="font-body text-sm mb-4">
                {{ $t('zones.' + dayZone) }}
              </div>
              <div class="flex flex-wrap gap-2">
                <radio-button
                  v-for="option in hours"
                  :key="option.start.valueOf()"
                  v-model="zoneHour"
                  @update:model-value="() => selectHour(dayZone, option.start)"
                  name="hours"
                  :value="`${dayZone}-${option.start}`"
                >
                  {{
                    format(option.start, 'HH:mm', {
                      in: tz(timezone)
                    })
                  }}
                </radio-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <step-navigation
      :disable-back="true"
      :disable-continue="!diners || !dateComputed || !hour || !zone"
      :continue-label="$t('booking.continue')"
      @continue="goToCustomerInfo()"
    />
  </div>
</template>

<script setup lang="ts">
import { tz } from '@date-fns/tz'
import {
  eachDayOfInterval,
  endOfMonth,
  format,
  isSameDay,
  startOfMonth
} from 'date-fns'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { LCalendar, LNumberInput, LSelect } from '@last/core-ui/paprika'
import eventSender from '@last/core-ui/scripts/eventSender'

import { bookingsApi } from '@/api/client'
import { useAuthStore } from '@/store/auth'
import { useBookingStore } from '@/store/booking'
import { useConfigStore } from '@/store/config'
import { useStepsStore } from '@/store/steps'

import BookingSteps from './common/BookingSteps.vue'
import RadioButton from './common/RadioButton.vue'
import StepNavigation from './common/StepNavigation.vue'

const router = useRouter()
const authStore = useAuthStore()
const configStore = useConfigStore()
const bookingStore = useBookingStore()
const stepsStore = useStepsStore()

const { diners, hour, date, zone, bookingId } = storeToRefs(bookingStore)
const { maxSeats, organizationLocations, locationId, timezone } =
  storeToRefs(authStore)
const { config } = storeToRefs(configStore)

const visibleMonth = ref(new Date())
const step = ref<'diners' | 'date' | 'time' | null>('diners')

const showMoreDiners = ref(false)
const unavailableDates = ref<Date[]>([])
const dayAvailability = ref<Record<string, { start: string; end: string }[]>>(
  {}
)

const locations = computed(() => {
  return organizationLocations.value?.map(location => ({
    label: location.name,
    value: location.id,
    metadata: {
      address: location.address
    }
  }))
})

const dinersComputed = computed({
  get: () => diners.value,
  set: value => bookingStore.setDiners(value)
})

const dateComputed = computed({
  get: () => (date.value ? new Date(date.value) : null),
  set: value => bookingStore.setDate(value?.toString() || null)
})

const hourComputed = computed({
  get: () => hour.value,
  set: value => bookingStore.setHour(value)
})

const zoneComputed = computed({
  get: () => zone.value,
  set: value => bookingStore.setZone(value)
})

const zoneHour = ref<string | null>(null)

const totalMaxSeats = computed(() => {
  let maxSeatsVal = Math.max(0, ...Object.values(maxSeats.value))
  const dinersLimit = config.value.dinersLimit
  if (dinersLimit) {
    maxSeatsVal = Math.min(maxSeatsVal, dinersLimit)
  }
  return maxSeatsVal
})

const dateFormatted = computed(() => {
  if (!dateComputed.value) {
    return ''
  }
  return format(dateComputed.value, 'EEEE, d MMMM', {
    in: tz(timezone.value)
  })
})

const hourFormatted = computed(() => {
  if (!hourComputed.value) {
    return ''
  }
  return format(hourComputed.value, 'HH:mm', {
    in: tz(timezone.value)
  })
})

function selectHour(zone: string, value: string) {
  hourComputed.value = value
  zoneComputed.value = zone
}

async function refreshFullSlots() {
  if (!diners.value) return

  const dates = await bookingsApi.getMonthAvailability({
    ignoreReservationId: bookingId.value ?? undefined,
    month: format(visibleMonth.value, 'yyyy-MM'),
    partySize: diners.value
  })

  unavailableDates.value = eachDayOfInterval(
    {
      start: startOfMonth(visibleMonth.value),
      end: endOfMonth(visibleMonth.value)
    },
    {
      in: tz(timezone.value)
    }
  ).filter(day => !dates.some(date => isSameDay(date, day)))
}

async function refreshHours() {
  if (!dateComputed.value || !diners.value) return

  dayAvailability.value = await bookingsApi.getDayAvailability({
    ignoreReservationId: bookingId.value ?? undefined,
    date: format(dateComputed.value, 'yyyy-MM-dd', {
      in: tz(timezone.value)
    }),
    partySize: diners.value
  })
}

function dinersSelected() {
  checkNextStep()
  showMoreDiners.value = false
}

function checkNextStep() {
  setTimeout(() => {
    if (!diners.value) {
      step.value = 'diners'
    } else if (!date.value) {
      step.value = 'date'
    } else if (!hour.value) {
      step.value = 'time'
    } else {
      step.value = null
    }
  }, 0)
}

async function goToCustomerInfo() {
  router.push({ name: 'customerInfo' })
  await eventSender.sendBookingOnGoing()
}

watch([diners, visibleMonth], () => {
  refreshFullSlots()
})

watch(dateComputed, async () => {
  refreshHours()
})

watch([dateComputed, hourComputed], () => {
  checkNextStep()
})

watch(dinersComputed, () => {
  if (showMoreDiners.value) return
  checkNextStep()
})

async function onLocationChange() {
  bookingStore.resetState()
  checkNextStep()
  await authStore.getLocationInfo()

  if (diners.value) {
    refreshFullSlots()
    refreshHours()
    if (diners.value > 0) {
      showMoreDiners.value = true
    }
  }
}

onMounted(async () => {
  stepsStore.setStep('reservation')

  checkNextStep()
  if (diners.value) {
    refreshFullSlots()
    refreshHours()
    if (diners.value > 0) {
      showMoreDiners.value = true
    }
  }
})
</script>
